/**
 * Игровые часы: переводят реальное время в годы жизни. Живут только в
 * режиме «Наблюдать» (core-loop-v2 §7; план 2d, Task 7): в режиме хода
 * `LifeScreen` их не создаёт, год идёт по кнопке «Следующий год».
 *
 * Цикл на `requestAnimationFrame`; накопленное время делится на
 * `yearMs / speed`. Догон ограничен одним годом за кадр: если вкладка
 * ушла в фон на десять минут, при возврате часы не «прокручивают» эти
 * минуты разом, а продолжают с того места, где остановились. Пауза
 * не копит время; после `resume` отсчёт идёт от текущего момента.
 *
 * Часы ничего не знают о движке: `onYear` вызывается синхронно и сам
 * решает, идёт ли год (стор не продвигает жизнь при ожидающем решении).
 * `skipToNextDecision` ставит 16× и возвращает прежнюю скорость, когда
 * `hasPending()` после очередного года отвечает «да».
 */
import { signal, type Signal } from '@preact/signals';

export type Speed = 1 | 2 | 4 | 8 | 12 | 16;

export const SPEEDS: readonly Speed[] = [1, 2, 4, 8, 12, 16];
export const SKIP_SPEED: Speed = 16;

/** Ожидаемая продолжительность жизни, если из датасета пришла ерунда. */
const FALLBACK_E0 = 72;

export interface Clock {
  speed: Signal<Speed>;
  paused: Signal<boolean>;
  /** Доля текущего года, 0 ≤ progress < 1. */
  progress: Signal<number>;
  start(): void;
  stop(): void;
  setSpeed(s: Speed): void;
  pause(): void;
  resume(): void;
  skipToNextDecision(): void;
}

export interface ClockOptions {
  /** Миллисекунд реального времени на один год при скорости 1. */
  yearMs: () => number;
  /** Вызывается на каждый прошедший год (не чаще одного раза за кадр). */
  onYear: () => void;
  /** Есть ли ожидающее решение — для `skipToNextDecision`. */
  hasPending?: () => boolean;
  /** Начальная скорость (из настроек); по умолчанию ×1. */
  speed?: Speed;
  now?: () => number;
  raf?: (cb: () => void) => void;
}

/** Сколько миллисекунд длится один год, чтобы жизнь уложилась в `durationMin`. */
export function yearMs(durationMin: number, e0: number): number {
  const life = Number.isFinite(e0) && e0 > 0 ? e0 : FALLBACK_E0;
  return (durationMin * 60_000) / life;
}

export function createClock(opts: ClockOptions): Clock {
  const now = opts.now ?? (() => performance.now());
  const raf =
    opts.raf ??
    ((cb: () => void) => {
      requestAnimationFrame(() => cb());
    });

  const speed = signal<Speed>(opts.speed ?? 1);
  const paused = signal(false);
  const progress = signal(0);

  let running = false;
  let last = 0;
  /** Накопленное время текущего года, мс. */
  let acc = 0;
  /** Скорость до `skipToNextDecision`, пока идёт перемотка. */
  let speedBeforeSkip: Speed | null = null;

  const msPerYear = () => Math.max(1, opts.yearMs() / speed.value);

  const updateProgress = () => {
    progress.value = Math.min(0.999, Math.max(0, acc / msPerYear()));
  };

  /**
   * Меняет скорость, сохраняя долю прожитого года, а не миллисекунды:
   * иначе при 1× → 16× накопленные 0.9 года превратились бы в 14 лет
   * и журнал «взорвался» бы пачкой событий.
   */
  const applySpeed = (s: Speed) => {
    const frac = acc / msPerYear();
    speed.value = s;
    acc = frac * msPerYear();
    updateProgress();
  };

  const frame = () => {
    const t = now();
    const dt = t - last;
    last = t;
    if (paused.value) return;

    const per = msPerYear();
    // Догон не больше одного года за кадр: длинная пауза вкладки — не «прокрутка».
    acc += Math.min(Math.max(0, dt), per);
    if (acc >= per) {
      acc -= per;
      opts.onYear();
      if (speedBeforeSkip !== null && opts.hasPending?.()) {
        const prev = speedBeforeSkip;
        speedBeforeSkip = null;
        applySpeed(prev);
      }
    }
    updateProgress();
  };

  const schedule = () => {
    raf(() => {
      if (!running) return;
      frame();
      schedule();
    });
  };

  return {
    speed,
    paused,
    progress,
    start() {
      if (running) return;
      running = true;
      last = now();
      schedule();
    },
    stop() {
      running = false;
    },
    setSpeed(s) {
      speedBeforeSkip = null;
      applySpeed(s);
    },
    pause() {
      paused.value = true;
    },
    resume() {
      last = now();
      paused.value = false;
    },
    skipToNextDecision() {
      if (speedBeforeSkip === null) speedBeforeSkip = speed.value;
      applySpeed(SKIP_SPEED);
    },
  };
}
